import React, { useEffect } from 'react';
import './ManagerPage.css';
import { ReactComponent as DataFlowSVG } from '../assets/data-flow.svg';
import { ReactComponent as ReportingSVG } from '../assets/reporting.svg';
import { ReactComponent as TeamChatSVG } from '../assets/team-chat.svg';
import { ReactComponent as DataFlowCircularSVG } from '../assets/data-flow-circular.svg';
import WaitlistSignup from './WaitlistSignup';

interface ManagerPageProps {
  onNavigate: (section: string) => void;
}

const ManagerPage: React.FC<ManagerPageProps> = ({ onNavigate }) => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          } else {
            entry.target.classList.remove("visible");
          }
        });
      },
      { threshold: 0.1 }
    );

    const sections = document.querySelectorAll("section");
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { 
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    document.querySelectorAll('.fade-up, .scale-in').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const smoothScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="manager-page">
      <div className="moving-lights">
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
        <div className="light"></div>
      </div>
      <div className="parallax-wrapper">
        <div className="floating-circles">
          <div className="floating-circle circle-1"></div>
          <div className="floating-circle circle-2"></div>
          <div className="floating-circle circle-3"></div>
          <div className="floating-circle circle-4"></div>
          <div className="floating-circle circle-5"></div>
        </div>
        {/* Hero Section - New Design */}
        <section className="hero">
          <div className="hero-content">
              <h1>Scale Your Leadership Impact</h1>
              <p>
              Naya calls employees on behalf of leadership to track work progress, identify blockers, and assess well-being; compiling the insights into concise briefings and action items.
              </p>
              <div className="leadership-metrics">
                <div className="metric">
                  <div className="metric-value">5x</div>
                  <div className="metric-label">Team Size Capacity</div>
                  <div className="metric-desc">Manage larger teams without losing personal touch</div>
                </div>
                <div className="metric">
                  <div className="metric-value">60%</div>
                  <div className="metric-label">Time Saved</div>
                  <div className="metric-desc">Focus on strategic decisions, not status updates</div>
                </div>
                <div className="metric">
                  <div className="metric-value">24/7</div>
                  <div className="metric-label">Team Insights</div>
                  <div className="metric-desc">Real-time visibility into team health and progress</div>
                </div>
              </div>
              <WaitlistSignup />
          </div>
        </section>

          {/* How It Works Section */}
          <section id="how-it-works" className="how-it-works">
          <h2>Your New Management Workflow</h2>
          <div className="process-steps">
            <div className="process-step fade-up">
              <div className="step-header">
                <div className="step-number">1</div>
                <h3>AI-Powered Check-ins</h3>
              </div>
              <p>Naya conducts natural conversations with your team members, adapting to their communication style and schedule.</p>
              <div className="step-example">
              "Hey Sarah, let's catch up on your week. How's the new feature coming along? Any blockers I should know about?"
              </div>
            </div>
            
            <div className="process-step fade-up">
              <div className="step-header">
                <div className="step-number">2</div>
                <h3>Smart Analysis</h3>
              </div>
              <p>Each conversation is analyzed to extract key insights, patterns, and potential issues across your entire team.</p>
              <div className="step-example">
                <ul>
                  <li>✓ Track Work Progress</li>
                  <li>✓ Identify Blockers</li>
                  <li>✓ Assess Well-being</li>
                  <li>✓ Recommended Actions</li>
                </ul>
              </div>
            </div>
            
            <div className="process-step fade-up">
              <div className="step-header">
                <div className="step-number">3</div>
                <h3>Flexible Leadership Updates</h3>
              </div>
              <p>Get a clear understanding of your team's status and priorities with concise multi-modal reports and actionable insights.</p>
              <div className="step-example">
                <div className="report-preview">
                  <div className="report-item">📊 Written Reports</div>
                  <div className="report-item">🎧 Audio Briefings</div>
                  <div className="report-item">🔔 Priority Alerts</div>
                  <div className="report-item">🔍 Team Health Metrics</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Move dashboard preview after workflow */}
        <section className="dashboard-preview">
          <h2>Your Command Center</h2>
          <div className="dashboard-grid">
            <div className="dashboard-card team-pulse">
              <h3>Team Pulse</h3>
              <div className="pulse-chart">
                <div className="chart-bar positive" style={{height: '80%'}}>
                  <span>Engagement</span>
                </div>
                <div className="chart-bar neutral" style={{height: '65%'}}>
                  <span>Progress</span>
                </div>
                <div className="chart-bar warning" style={{height: '30%'}}>
                  <span>Blockers</span>
                </div>
              </div>
            </div>
            <div className="dashboard-card priorities">
              <h3>Priority Actions</h3>
              <ul className="action-list">
                <li className="high">Database migration blocked - Team A needs review</li>
                <li className="medium">Recognition opportunity - Sarah's API launch</li>
                <li className="low">Schedule quarterly planning session</li>
              </ul>
            </div>
            <div className="dashboard-card insights">
              <h3>AI Insights</h3>
              <div className="insight-items">
                <div className="insight">
                  <span className="trend positive">↑</span>
                  <p>Team morale improved 15% after recent launch</p>
                </div>
                <div className="insight">
                  <span className="trend warning">!</span>
                  <p>3 team members mentioned timeline concerns</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="value-props">
          <div className="value-grid">
            <div className="value-item">
              <div className="value-icon">🎯</div>
              <h3>Focus on What Matters</h3>
              <p>Let AI handle routine check-ins while you focus on strategic decisions and team growth</p>
            </div>
            <div className="value-item">
              <div className="value-icon">📈</div>
              <h3>Scale Your Impact</h3>
              <p>Maintain high-quality oversight as your team grows with AI-powered insights</p>
            </div>
            <div className="value-item">
              <div className="value-icon">🔍</div>
              <h3>Deep Understanding</h3>
              <p>Get comprehensive insights into team dynamics, morale, and productivity</p>
            </div>
            <div className="value-item">
              <div className="value-icon">⚡️</div>
              <h3>Instant Action</h3>
              <p>Address issues before they escalate with real-time alerts and recommendations</p>
            </div>
          </div>
        </section>

        {/* Wavy Lines before Impact Section */}
        <div className="wavy-divider">
          <svg className="wavy-line front" viewBox="0 0 2400 100" preserveAspectRatio="none">
            <path d="M0,40 C200,80 400,0 600,40 C800,80 1000,0 1200,40 C1400,80 1600,0 1800,40 C2000,80 2200,0 2400,40" />
          </svg>
          <svg className="wavy-line back" viewBox="0 0 2400 100" preserveAspectRatio="none">
            <path d="M0,60 C200,20 400,100 600,60 C800,20 1000,100 1200,60 C1400,20 1600,100 1800,60 C2000,20 2200,100 2400,60" />
          </svg>
        </div>

        {/* Impact Section - Now comes after How It Works */}
        <section id="impact" className="impact">
          <h2>The Cost of Traditional Management</h2>
          <div className="impact-content">
            <div className="impact-row fade-up">
              <div className="impact-text">
                <h3>Time Drain on Leadership</h3>
                <p>Traditional management methods consume valuable time that could be spent on strategic initiatives and meaningful mentorship.</p>
              </div>
              <div className="impact-stat">
                <div className="impact-number">40%</div>
                <p>Of a manager's time spent on routine check-ins and status updates</p>
              </div>
            </div>
            <div className="impact-row fade-up">
              <div className="impact-text">
                <h3>Limited Team Scaling</h3>
                <p>The traditional span of control limits organizational growth and creates unnecessary management layers.</p>
              </div>
              <div className="impact-stat">
                <div className="impact-number">7-9</div>
                <p>Maximum direct reports traditionally manageable</p>
              </div>
            </div>
            <div className="impact-row fade-up">
              <div className="impact-text">
                <h3>Decision-Making Delays</h3>
                <p>Traditional management creates bottlenecks in decision-making, slowing down team progress and reducing organizational agility.</p>
              </div>
              <div className="impact-stat">
                <div className="impact-number">73%</div>
                <p>Of employees report delayed decisions due to management bottlenecks</p>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section id="benefits" className="benefits">
          <div className="benefits-container">
            <div className="benefits-intro fade-up">
              <h2>Unlock True Leadership</h2>
              <p>Transform your management approach and empower your team to reach their full potential.</p>
            </div>
            <div className="benefits-grid-new">
              <div className="benefit-item scale-in">
                <div className="benefit-icon">🚀</div>
                <h3>Scale Teams Efficiently</h3>
                <p>Manage 5x larger teams without sacrificing quality of oversight</p>
              </div>
              <div className="benefit-item scale-in">
                <div className="benefit-icon">⚡️</div>
                <h3>Boost Productivity</h3>
                <p>Eliminate repetitive check-ins and focus on strategic initiatives</p>
              </div>
              <div className="benefit-item scale-in">
                <div className="benefit-icon">📊</div>
                <h3>Data-Driven Insights</h3>
                <p>Make informed decisions based on comprehensive team analytics</p>
              </div>
              <div className="benefit-item scale-in">
                <div className="benefit-icon">🌱</div>
                <h3>Foster Growth</h3>
                <p>Enable team members to become advocates of their own success</p>
              </div>
            </div>
          </div>
        </section>

        {/* Feedback Section */}
        <section id="feedback" className="feedback">
          <div className="feedback-content">
            <h2>Built With Your Input</h2>
            <p>Naya evolves with your feedback. Feel free to share your thoughts or 
              ask any questions about the future of team communication.</p>
            <a
              href="https://mail.google.com/mail/?view=cm&fs=1&to=team@meetnaya.com"
              className="cta-button"
              target="_blank"
              rel="noopener noreferrer"
            >
              Contact Us
            </a>
          </div>
        </section>

        {/* Built By Section */}
        <section id="built-by" className="built-by">
          <h2>Built by AI enthusiasts from</h2>
          <div className="companies-grid">
            <div className="company-logo">
              <img src="/companies/microsoft.svg" alt="Microsoft" />
            </div>
            <div className="company-logo">
              <img src="/companies/amazon.svg" alt="Amazon" />
            </div>
            <div className="company-logo">
              <img src="/companies/berkeley.svg" alt="UC Berkeley" />
            </div>
            <div className="company-logo">
              <img src="/companies/stanford.svg" alt="Stanford" />
            </div>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="footer">
          <p>&copy; 2025 Naya AI. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
};

export default ManagerPage;
